import type {
  ServiceWorkerMessage,
  StartPdfDownloadMessage,
  CheckPdfCacheMessage,
  GetCachedPdfMessage,
  PdfDownloadProgressMessage,
  PdfDownloadCompleteMessage,
  PdfDownloadErrorMessage
} from './types'

/**
 * Sends a message to the Service Worker to start downloading PDFs in background
 * @param token - Authentication token
 * @param graphqlEndpoint - GraphQL API endpoint
 */
export const startPdfDownload = (token: string, graphqlEndpoint: string): void => {
  if (!navigator.serviceWorker?.controller) {
    console.warn('SW Manager: No service worker controller available')
    return
  }

  if (/\/reviews\/\d+\/view/.test(window.location.pathname)) {
    console.info('SW Manager:Skipping PDF download for review view')
    return
  }

  const message: StartPdfDownloadMessage = {
    type: 'START_PDF_DOWNLOAD',
    token,
    graphqlEndpoint
  }

  navigator.serviceWorker.controller.postMessage(message)
  console.log('SW Manager: Sent START_PDF_DOWNLOAD message to service worker')
}

/**
 * Checks if a PDF is cached in IndexedDB
 * @param id - Job/Project ID
 * @returns Promise with cache status
 */
export const checkPdfCache = async (id: number): Promise<boolean> => {
  if (!navigator.serviceWorker?.controller) {
    console.warn('SW Manager: No service worker controller available')
    return false
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel()

    messageChannel.port1.onmessage = (event: MessageEvent<{ isCached?: boolean }>) => {
      resolve(typeof event.data.isCached === 'boolean' ? event.data.isCached : false)
    }

    const message: CheckPdfCacheMessage = {
      type: 'CHECK_PDF_CACHE',
      id
    }

    navigator.serviceWorker.controller!.postMessage(message, [messageChannel.port2])
  })
}

/**
 * Gets a cached PDF from IndexedDB via Service Worker
 * @param id - Job/Project ID
 * @returns Promise with cached PDF data or null
 */
export const getCachedPdf = async (id: number): Promise<Blob | null> => {
  if (!navigator.serviceWorker?.controller) {
    console.warn('SW Manager: No service worker controller available')
    return null
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel()

    messageChannel.port1.onmessage = (event: MessageEvent<{ pdf?: { blob?: Blob } }>) => {
      const pdf = event.data?.pdf
      resolve(pdf && pdf.blob instanceof Blob ? pdf.blob : null)
    }

    const message: GetCachedPdfMessage = {
      type: 'GET_CACHED_PDF',
      id
    }

    navigator.serviceWorker.controller!.postMessage(message, [messageChannel.port2])
  })
}

/**
 * Listens to messages from the Service Worker
 * @param callback - Callback function to handle messages
 * @returns Cleanup function to remove listener
 */
export const listenToSWMessages = (
  callback: (message: ServiceWorkerMessage) => void
): (() => void) => {
  const handler = (event: MessageEvent<ServiceWorkerMessage>) => {
    callback(event.data)
  }

  navigator.serviceWorker.addEventListener('message', handler)

  return () => {
    navigator.serviceWorker.removeEventListener('message', handler)
  }
}

/**
 * Type guards for Service Worker messages
 */
export const isProgressMessage = (
  message: ServiceWorkerMessage
): message is PdfDownloadProgressMessage => {
  return message.type === 'PDF_DOWNLOAD_PROGRESS'
}

export const isCompleteMessage = (
  message: ServiceWorkerMessage
): message is PdfDownloadCompleteMessage => {
  return message.type === 'PDF_DOWNLOAD_COMPLETE'
}

export const isErrorMessage = (
  message: ServiceWorkerMessage
): message is PdfDownloadErrorMessage => {
  return message.type === 'PDF_DOWNLOAD_ERROR'
}
